import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin (support both capitalized and lowercase)
  const isAdmin = user.role === 'Admin' || user.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-4">
        <div className="max-w-2xl w-full text-center space-y-8">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-destructive/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-destructive/20 to-destructive/10 p-8 rounded-full">
                <ShieldAlert className="h-20 w-20 text-destructive" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-destructive to-destructive/60 bg-clip-text text-transparent">
              Access Denied
            </h1>
            <p className="text-xl text-muted-foreground">
              You don't have permission to access this page
            </p>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              This page is restricted to administrators only. If you believe you should have access, please contact your system administrator.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-center pt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25"
            >
              <Home className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
